import React, { useEffect, useState } from 'react';
import {
  SafeAreaView,
  ScrollView,
  StyleSheet,
  View,
  TouchableOpacity,
  Text,
  ActivityIndicator,
} from 'react-native';
import Modal from 'react-native-modal';
import { CheckBox, Icon } from 'react-native-elements';
import { get as _get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import Divider from '../../components/Divider';
import config from '../../lib/config';
import { selectContest } from '../../store/contestSlice';
import { RootState } from '../../store/rootReducer';
import {
  addDontShowContest,
  removeDontShowContest,
} from '../../store/appSlice';
import { RouteNames } from '../../navigation';
import { trackContestView } from '../../lib/analytics';
import CircleButton from '../CircleButton';

export interface Props {
  onClose: () => void;
  navigation: any;
}

const ContestOverlay: React.FC<Props> = ({ onClose, navigation }) => {
  const dispatch = useDispatch();
  const [dontShow, setDontShow] = useState(false);
  const [dontShowDirty, setDontShowDirty] = useState(false);

  const { loading: contestLoading, data: contestData } = useSelector((state) =>
    selectContest(state as RootState),
  );

  useEffect(() => {
    if (contestData && dontShowDirty) {
      const params = { id: contestData.id };
      if (dontShow) {
        dispatch(addDontShowContest(params));
      } else {
        dispatch(removeDontShowContest(params));
      }
    }
  }, [dispatch, dontShow, contestData, dontShowDirty, setDontShowDirty]);

  useEffect(() => {
    trackContestView(contestData);
  }, [contestData]);

  const handleOnDontShowContest = () => {
    setDontShow((current) => !current);
    setDontShowDirty(true);
  };

  const handleOnEnterContest = () => {
    navigation.navigate(RouteNames.Contest);
    onClose && onClose();
  };

  return (
    <Modal
      isVisible
      backdropColor={'#000000'}
      backdropOpacity={0.5}
      hideModalContentWhileAnimating
      style={styles.overlay}
      onBackdropPress={onClose}
    >
      <View style={styles.topOverlay}>
        <View style={styles.closeButtonContainer}>
          <TouchableOpacity onPress={onClose}>
            <Icon
              tvParallaxProperties={null}
              activeOpacity={0.5}
              name={'close'}
              size={30}
              color={'#505050'}
            />
          </TouchableOpacity>
        </View>
      </View>
      <SafeAreaView>
        <ScrollView
          contentInsetAdjustmentBehavior="automatic"
          style={styles.scrollView}
        >
          {contestLoading ? (
            <ActivityIndicator style={styles.loadingIndicator} />
          ) : (
            <>
              <View
                style={[styles.featureHeadingContainer, styles.sectionStart]}
              >
                <Text style={styles.featureHeading}>
                  {_get(contestData, 'title', 'Contest')}
                </Text>
                <Text>{_get(contestData, 'description', '')}</Text>
              </View>
              <CheckBox
                title="Don't show again for this contest"
                checked={dontShow}
                onPress={handleOnDontShowContest}
                containerStyle={styles.checkboxContainerStyle}
                textStyle={styles.checkboxTextStyle}
                iconType="ionicon"
                uncheckedIcon="square-outline"
                checkedIcon="checkbox-outline"
                checkedColor={config.theme.brandColor}
              />
              <Divider />
              <View style={styles.buttonContainer}>
                <CircleButton
                  iconName={'arrow-forward'}
                  onPress={handleOnEnterContest}
                />
                <Text style={styles.circleButtonText}>Enter Contest</Text>
              </View>
            </>
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    margin: 0,
    marginTop: 40,
    height: '100%',
  },
  closeButtonContainer: {
    height: '100%',
    zIndex: 1000,
    flex: 1,
    alignContent: 'center',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginStart: 10,
  },
  buttonContainer: {
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    padding: 0,
    marginTop: 0,
  },
  circleButtonText: {
    marginTop: -10,
  },
  topOverlay: {
    flex: 1,
    position: 'absolute',
    opacity: 0.5,
    borderTopStartRadius: 10,
    borderTopEndRadius: 10,
    overflow: 'hidden',
    right: 0,
    top: 0,
    width: '100%',
    height: 40,
    fontWeight: 'bold',
    zIndex: 1001,
    backgroundColor: '#ffffff',
    padding: 0,
    marginTop: 0,
  },
  scrollView: {
    backgroundColor: '#ffffff',
    marginHorizontal: 0,
    borderRadius: 10,
    minHeight: '100%',
  },
  loadingIndicator: {
    marginTop: 20,
  },
  featureHeadingContainer: {
    marginBottom: 5,
    marginHorizontal: 20,
  },
  sectionStart: {
    marginTop: 50,
    marginBottom: 20,
  },
  featureHeading: {
    fontSize: config.theme.headingFontSize,
    fontWeight: config.theme.headingFontWeight,
    color: config.theme.headingTextColor,
    marginBottom: 10,
  },
  checkboxContainerStyle: {
    backgroundColor: 'transparent',
    paddingVertical: 2,
    borderWidth: 0,
    margin: 0,
    padding: 0,
    marginLeft: 20,
    marginTop: 50,
    marginBottom: 10,
  },
  checkboxTextStyle: {
    fontWeight: '400',
  },
});

export default ContestOverlay;
